import { Injectable } from '@angular/core';
import { fromEvent, merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { LoggerService } from './logger.service';

/**
 * InactivityMonitorService - Monitorea actividad del usuario
 * 
 * Responsabilidades (Single Responsibility Principle):
 * 1. Detectar actividad del usuario (mouse, teclado, etc.)
 * 2. Rastrear tiempo de inactividad
 * 3. Determinar cuándo se requiere validación de PIN
 * 
 * Principios aplicados:
 * - SRP: Una sola responsabilidad (monitoreo de actividad/inactividad)
 * - OCP: Abierto para extensión (configuración de tiempos)
 * - DRY: Sin duplicación de lógica
 * - Clean Code: Nombres descriptivos, métodos pequeños
 */
@Injectable({
  providedIn: 'root'
})
export class InactivityMonitorService {
  
  // Configuración de tiempos (en minutos) - F��cilmente configurable
  private readonly INACTIVITY_TIMEOUT = 30; // 30 minutos de inactividad
  // Clave de localStorage - Centralizada
  private readonly LAST_ACTIVITY_KEY = 'lastActivityTimestamp';
  
  private logger: LoggerService;

  constructor() {
    this.logger = new LoggerService();
    this.initializeActivityTracking();
    this.logger.log('✅ [INACTIVITY-MONITOR] Servicio inicializado');
  }

  /**
   * Inicializa el tracking de actividad del usuario
   * Escucha eventos de mouse, teclado, scroll, etc.
   */
  private initializeActivityTracking(): void {
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    merge(...activityEvents.map(event => fromEvent(document, event)))
      .pipe(debounceTime(1000)) // Evitar actualizar en cada movimiento
      .subscribe(() => this.updateLastActivity());

    this.logger.log('🔍 [INACTIVITY-MONITOR] Tracking de actividad inicializado');
  }

  /**
   * Actualiza el timestamp de última actividad
   */
  private updateLastActivity(): void {
    localStorage.setItem(this.LAST_ACTIVITY_KEY, Date.now().toString());
  }

  /**
   * Obtiene el timestamp de última actividad
   */
  private getLastActivityTimestamp(): number {
    const stored = localStorage.getItem(this.LAST_ACTIVITY_KEY);
    if (stored) {
      return parseInt(stored, 10);
    }
    
    // Si no hay registro, usar timestamp actual y guardarlo
    const now = Date.now();
    this.updateLastActivity();
    return now;
  }

  /**
   * Verifica si se requiere PIN por inactividad
   * @returns true si han pasado más de INACTIVITY_TIMEOUT minutos sin actividad
   */
  public shouldRequestPinForInactivity(): boolean {
    const inactiveMinutes = this.getInactivityMinutes();
    const shouldRequest = inactiveMinutes > this.INACTIVITY_TIMEOUT;
    
    if (shouldRequest) {
      this.logger.warn(`⚠️ [INACTIVITY-MONITOR] Inactividad detectada: ${Math.floor(inactiveMinutes)} min > ${this.INACTIVITY_TIMEOUT} min`);
    }
    
    return shouldRequest;
  }

  /**
   * Obtiene los minutos de inactividad
   */
  public getInactivityMinutes(): number {
    const lastActivity = this.getLastActivityTimestamp();
    const now = Date.now();
    return (now - lastActivity) / (1000 * 60);
  }

  /**
   * Marca que el PIN fue validado
   * Reinicia la actividad
   */
  public markPinValidated(): void {
    this.updateLastActivity();
    this.logger.log('✅ [INACTIVITY-MONITOR] PIN validado - Actividad reiniciada');
  }

  /**
   * Reinicia la actividad (usado en logout)
   */
  public reset(): void {
    localStorage.removeItem(this.LAST_ACTIVITY_KEY);
    this.logger.log('🔄 [INACTIVITY-MONITOR] Actividad reiniciada');
  }

  /**
   * Obtiene la configuración actual
   * Útil para debugging y testing
   */
  public getConfiguration(): { inactivityTimeout: number } {
    return {
      inactivityTimeout: this.INACTIVITY_TIMEOUT
    };
  }
}
